import assert from 'assert';

import {CustomContextPackage, makeReactContext} from 'lib/makeReactContext';
import documentStore from 'notebook/documents/DocumentStore';
import {UpdateNoteOptions} from 'notebook/notes/NoteConstants';
import {StructuredTableMapping} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {Note} from 'notebook/util/NoteUtilConstants';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';

type Store = typeof documentStore;

export interface TableNoteContextInterface {
  fileId: string;
  tableNamespace: string;
  getNote: () => Note;
  useNoteSelector: <T>(selector: (note: Note) => T) => T;
  updateNote: (
    note: Note,
    opts?: UpdateNoteOptions,
    structuredTableMappings?: Array<StructuredTableMapping>
  ) => void;
}

/**
 * This is intended to be a generic interface that is agnostic to the store.
 * Different providers can encapsulate the varying ways to interact with stores.
 */
const NullableTableNoteContext = makeReactContext<TableNoteContextInterface | null>(
  () => null,
  'TableNoteContext'
);

export const TableNoteContext: CustomContextPackage<TableNoteContextInterface> = {
  ...NullableTableNoteContext,
  useContext: () => {
    const context = NullableTableNoteContext.useContext();
    assert(context != null, 'TableNoteContext must be used within a TableNoteContext Provider');
    return context;
  },
};

export function useNullableTableNoteContext(): TableNoteContextInterface | null {
  return NullableTableNoteContext.useContext();
}

export function createNoteSelector(store: Store, fileId: string) {
  return <T,>(selector: (note: Note) => T): T =>
    useStoreSelector<Store, T>(store, (store) => {
      const note = store.getNote(fileId);
      return selector(note);
    });
}
